// API client for the AI assistant ("Ask Roo AI") — free-text poultry Q&A,
// disease detection from a photo, and weight estimation from a photo.
//
// This is a SECOND axios instance, deliberately separate from
// utils/axiosinstace.js: the AI chatbot is its own service on its own base
// path, while the rest of the app talks to the Node API at /api/v1. Only the
// assistant's calls go through here. Auth is the same bearer token, and a
// 401/403 refreshes against the main API exactly as axiosinstace.js does.
//
// PORTED FROM: ipoultry-mobile src/chatbot.js + src/chatlist.js. The request
// bodies, the `type` discriminator and the `{ reply }` response envelope are
// all taken from that shipped client.
//
// ROUTE NAMES — verified against the deployed host by probing for DRF's 401
// ("exists, needs auth") versus 404 ("no such route"). Note `messages/` is
// PLURAL here: the React Native client posts to `message/`, which 404s.
import axios from "axios";
import { env } from "../pages/const";

// Relative by design. In production the SPA and this service share an origin
// (ipoultry.digiterrain.live serves the app at /chatbot/ and the API at
// /chatbot/api/), and in development src/setupProxy.js forwards the same path
// — so the browser is never cross-origin. That matters here: unlike the Node
// API, this service sends no Access-Control-* headers, so a direct absolute
// URL from localhost is blocked outright. Override for a host that genuinely
// is cross-origin, and enable CORS there.
export const AI_BASE_URL = process.env.REACT_APP_CHATBOT_API_BASE_URL || "/chatbot/api";

// Origin of the chatbot service, used to absolutise relative image URLs the
// history endpoint may return.
const AI_ORIGIN = (() => {
  try {
    const base = typeof window !== "undefined" ? window.location.origin : undefined;
    return new URL(AI_BASE_URL, base).origin;
  } catch (e) {
    return "";
  }
})();

// Every path the assistant uses, in one place.
export const ROUTES = {
  chats: "/chats/",
  chat: (id) => `/chats/${id}/`,
  message: (id) => `/chats/${id}/messages/`,
};

// The breeds offered for weight estimation. Hardcoded to match the mobile app;
// move to the backend when it can serve the list.
export const BREEDS = [
  "Broiler",
  "Kadaknath",
  "Aseel",
  "Vanaraja",
  "Giriraja",
  "Rhode Island Red",
  "Leghorn",
];

const aiInstance = axios.create({ baseURL: AI_BASE_URL });

aiInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Let the browser write its own multipart boundary. Setting the header by
  // hand (as the React Native client does) produces a boundary-less
  // Content-Type here and the upload is rejected.
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  } else {
    config.headers["Content-Type"] = "application/json";
  }
  return config;
});

// Single-flight token refresh, mirroring utils/axiosinstace.js. Deliberately
// does NOT redirect to /login on failure: the main instance already owns that,
// and throwing the user out of a half-typed question is worse than showing the
// error in the panel.
let refreshing = null;

aiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if ((status === 401 || status === 403) && original && !original._retry) {
      original._retry = true;
      if (!refreshing) {
        const refreshToken = localStorage.getItem("refreshToken");
        refreshing = axios
          .post(`${env.produrl}/refresh_token`, { refreshToken })
          .then((res) => {
            const next = res.data?.accessToken;
            if (!next) throw new Error("No access token in refresh response");
            localStorage.setItem("token", next);
            return next;
          })
          .finally(() => {
            refreshing = null;
          });
      }
      try {
        const token = await refreshing;
        original.headers.Authorization = `Bearer ${token}`;
        return aiInstance(original);
      } catch (e) {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// A history image may come back as a relative path; <img> needs an absolute one.
export const resolveImageUrl = (url) => {
  if (!url) return null;
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  return `${AI_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
};

// ---- conversations ------------------------------------------------------

export const listChats = () => aiInstance.get(ROUTES.chats).then((r) => r.data);

export const createChat = () => aiInstance.post(ROUTES.chats).then((r) => r.data);

export const getChat = (chatId) => aiInstance.get(ROUTES.chat(chatId)).then((r) => r.data);

export const deleteChat = (chatId) => aiInstance.delete(ROUTES.chat(chatId));

// ---- messages -----------------------------------------------------------

// Free-text question.
export const sendText = (chatId, message) =>
  aiInstance.post(ROUTES.message(chatId), { message, type: "text" }).then((r) => r.data);

// Photo analysis. `mode` is "disease" or "weight"; weight also carries the
// breed and the flock age in days.
export const sendImage = (chatId, { file, mode, breed, days }) => {
  const form = new FormData();
  form.append("type", mode);
  form.append("image", file, file.name);
  if (mode === "weight") {
    form.append("breed", breed);
    form.append("days", String(days));
  }
  return aiInstance.post(ROUTES.message(chatId), form).then((r) => r.data);
};

// Normalises a stored message into the shape the panel renders.
export const toViewMessage = (m) => ({
  id: String(m.id),
  from: m.role === "user" ? "user" : "bot",
  text: m.content || "",
  image: resolveImageUrl(m.image),
});

export default aiInstance;
